import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Marquee } from '@sorare/core/src/atoms/container/Marquee';
import { S26 } from '@sorare/core/src/atoms/icons/S26';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { SimpleRadio } from '@sorare/core/src/atoms/inputs/SimpleRadio';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS, BrandXL, BrandXS } from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useQuickplayAssets } from '@sorare/core/src/hooks/useQuickplayAssets';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  MonetaryAmount,
  monetaryAmountFragment,
} from '@sorare/core/src/lib/monetaryAmount';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { ESTIMATED_CASH_PRIZE_IN_USER_CURRENCY } from 'components/quickPlay/QuickPlayTutorialDialog/utils';

import type { QuickplayLimitedPlusMode_conversionCredit } from './__generated__/index.graphql';
import background from './assets/background.webp';
import footballPlayers from './assets/footballPlayers.webp';
import players from './assets/players.webp';

const Root = styled.label`
  width: 100%;
  position: relative;
  max-width: calc(80 * var(--unit));
  & > * {
    width: 100%;
  }
  transition: all 0.1s ease-in-out;
  &:hover {
    transform: scale(1.02);
  }
  cursor: pointer;
  color: var(--c-white);
  overflow: hidden;
`;

const RadioWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  left: var(--double-unit);
  color: var(--c-nd-600);
`;

const PlayersImg = styled.img`
  width: 120%;
  max-height: 23vh;
  object-fit: cover;
  object-position: top;
  @media ${tabletAndAbove} {
    max-height: unset;
  }

  @media ${tabletAndAbove} {
    width: 80%;
  }
`;

const BottomBanner = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: var(--quadruple-unit);
  width: 100%;
  color: var(--c-limited-tertiary);
  background: var(--main-color, #757aeb);
`;

const InnerWrapper = styled(Vertical)`
  position: relative;
  z-index: 0;
  padding-top: var(--double-unit);
  background: var(--bg) no-repeat top center / cover;
  gap: var(--double-unit);
  @media ${tabletAndAbove} {
    gap: calc(5 * var(--unit));
  }
`;

const HeaderChip = styled(Horizontal)`
  --color: var(--c-blue-400);
  border-radius: var(--unit);
  box-shadow:
    0 0 12px 0 rgba(var(--c-rgb-blue-400), 0.5) inset,
    0 0 8px 0 var(--color);
  padding: var(--unit) var(--intermediate-unit);
  border: 1px solid var(--color);
`;

const DiscountHeaderChip = styled(HeaderChip)`
  --color: #87f6ab;
`;

const TitleWrapper = styled(Vertical)`
  text-wrap: balance;
  text-align: center;
`;

const StyledBrandXS = styled(BrandXS)`
  line-height: 30px;
`;

const SecondRow = styled(Horizontal)`
  align-items: baseline;
`;

const StyledNeonEdge = styled(NeonEdge)`
  background: var(--c-black);
`;

type Props = {
  selected: boolean;
  onSelect: () => void;
  conversionCredit: Nullable<QuickplayLimitedPlusMode_conversionCredit>;
};

const playerImgBySport = {
  [Sport.NBA]: players,
  [Sport.BASEBALL]: players,
  [Sport.FOOTBALL]: footballPlayers,
};

export const QuickplayLimitedPlusMode = ({
  selected,
  onSelect,
  conversionCredit,
}: Props) => {
  const quickplayAssets = useQuickplayAssets();
  const { sport } = useSportContext();
  const { main: maxDiscountWithConversion } = useAmountWithConversion({
    monetaryAmount: conversionCredit?.maxDiscount || MonetaryAmount.zero,
    hideFractionDigits: true,
  });

  const mainColor = quickplayAssets.mainColor || '#757aeb';

  return (
    <Root
      className={classNames({ selected })}
      style={{ '--main-color': mainColor } as CSSProperties}
    >
      <Bezel
        borderColor={selected ? mainColor : 'var(--c-nd-150)'}
        className={classNames({ selected })}
      >
        <InnerWrapper
          center
          style={
            {
              '--bg': `url(${quickplayAssets.limitedPlugBg || background})`,
            } as CSSProperties
          }
        >
          <StyledNeonEdge color="#E9FE92">
            <DiscountHeaderChip>
              <img
                src={`${FRONTEND_ASSET_HOST}/rewards/credit.png`}
                width={24}
                height={24}
                alt=""
              />
              <BrandS>
                <FormattedMessage
                  id="ChooseGameMode.discount"
                  defaultMessage="{amount} off*"
                  values={{ amount: maxDiscountWithConversion }}
                />
              </BrandS>
            </DiscountHeaderChip>
          </StyledNeonEdge>

          <Vertical center gap={0.5}>
            <TitleWrapper center>
              {!!conversionCredit && (
                <FormattedMessage
                  id="nba.LimitedPlusMode.rewardsMessage2"
                  defaultMessage="<a><row1>{cardIcon} Start with 1 card for</row1></a><row2><b>{cashAmount}</b></row2>"
                  values={{
                    cardIcon: <ScarcityIcon rarity={Rarity.limited} size={2} />,
                    cashAmount: (
                      <FormattedNumber
                        style="currency"
                        currency="USD"
                        value={ESTIMATED_CASH_PRIZE_IN_USER_CURRENCY}
                        currencyDisplay="narrowSymbol"
                        maximumFractionDigits={0}
                      />
                    ),
                    row1: chunks => <Horizontal gap={0.5}>{chunks}</Horizontal>,
                    row2: chunks => <SecondRow gap={0.5}>{chunks}</SecondRow>,
                    a: chunks => <BrandXS>{chunks}</BrandXS>,
                    b: chunks => (
                      <BrandXL
                        className="text-gradient-color"
                        style={
                          {
                            '--text-gradient-color': 'var(--c-gradient-gold)',
                          } as CSSProperties
                        }
                      >
                        {chunks}
                      </BrandXL>
                    ),
                  }}
                />
              )}
            </TitleWrapper>
            <PlayersImg src={playerImgBySport[sport]} alt="" />
          </Vertical>
          <RadioWrapper>
            <SimpleRadio
              id="limited-radio"
              checked={selected}
              checkedColor="var(--c-white)"
              onClick={onSelect}
            />
          </RadioWrapper>
          <BottomBanner>
            <Marquee gap={1} duration="3s" disableAnimation={!selected}>
              <Horizontal center gap={0.5}>
                <S26 />
                <StyledBrandXS>
                  <FormattedMessage {...glossary.quickPlay} />
                </StyledBrandXS>
              </Horizontal>
            </Marquee>
          </BottomBanner>
        </InnerWrapper>
      </Bezel>
    </Root>
  );
};

QuickplayLimitedPlusMode.fragments = {
  conversionCredit: gql`
    fragment QuickplayLimitedPlusMode_conversionCredit on ConversionCredit {
      id
      maxDiscount {
        ...MonetaryAmountFragment_monetaryAmount
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<QuickplayLimitedPlusMode_conversionCredit>,
};
